import { callAPI } from "../helper/apiUtils";
import webPushConfig from "../config/webPushConfig";

const DISABLED_KEY = "kjuup_web_push_disabled";
const SUBSCRIBED_KEY = "kjuup_web_push_subscribed";
const HOURS_KEY = "kjuup_notification_hours";
const SW_PATH = "/kjuup-sw.js?v=9";
const AUTO_CLOSE_MS = 5000;
const BERLIN_TZ = "Europe/Berlin";

// undefined = not loaded this session, null = no saved preference, object = preference
let _hoursPref = undefined;

let _clickHandler = null;
let _swListenerBound = false;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isWebPushSupported() {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

export function getNotificationPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

export function isWebPushDisabledByUser() {
  try {
    return localStorage.getItem(DISABLED_KEY) === "1";
  } catch (e) {
    return false;
  }
}

export function hasActiveWebPushSubscription() {
  try {
    return localStorage.getItem(SUBSCRIBED_KEY) === "1";
  } catch (e) {
    return false;
  }
}

function normalizeHoursPref(pref) {
  if (!pref || typeof pref !== "object") return null;
  const type = pref.type;
  if (type !== "everyday" && type !== "weekdays" && type !== "custom") return null;
  let custom = pref.customPreference;
  if (typeof custom === "string") {
    try {
      custom = JSON.parse(custom);
    } catch (e) {
      custom = null;
    }
  }
  return {
    type,
    startTime: pref.startTime || null,
    endTime: pref.endTime || null,
    customPreference: custom && typeof custom === "object" ? custom : null,
  };
}

export function setNotificationHoursPref(pref) {
  _hoursPref = normalizeHoursPref(pref);
  try {
    if (_hoursPref === null) localStorage.setItem(HOURS_KEY, "null");
    else localStorage.setItem(HOURS_KEY, JSON.stringify(_hoursPref));
  } catch (e) { /* ignore */ }
}

function readHoursPref() {
  if (_hoursPref !== undefined) return _hoursPref;
  try {
    const raw = localStorage.getItem(HOURS_KEY);
    if (raw == null) return undefined;
    if (raw === "null") {
      _hoursPref = null;
      return null;
    }
    _hoursPref = normalizeHoursPref(JSON.parse(raw));
    return _hoursPref;
  } catch (e) {
    return undefined;
  }
}

function berlinClock() {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: BERLIN_TZ,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const get = (type) => (parts.find((p) => p.type === type) || {}).value || "";
  const day = get("weekday").replace(/\./g, "").toLowerCase().slice(0, 3);
  let hour = get("hour");
  if (hour === "24") hour = "00";
  const time = `${hour.padStart(2, "0")}:${get("minute").padStart(2, "0")}`;
  return { day, time };
}

function isTimeInRange(currentTimeStr, start, end) {
  if (!start || !end) return false;
  return currentTimeStr >= start && currentTimeStr < end;
}

/**
 * Same window as backend `notificationPreferenceFind` (Europe/Berlin).
 * No saved preference → allow, matching the backend.
 */
export function isWithinNotificationHours() {
  const pref = readHoursPref();
  if (!pref) return true;

  const { day, time } = berlinClock();

  if (pref.type === "everyday") {
    return isTimeInRange(time, pref.startTime, pref.endTime);
  }
  if (pref.type === "weekdays") {
    const isWeekday = day !== "sat" && day !== "sun";
    return isWeekday && isTimeInRange(time, pref.startTime, pref.endTime);
  }
  if (pref.type === "custom") {
    const dayPref = pref.customPreference && pref.customPreference[day];
    if (!dayPref || !dayPref.startTime || !dayPref.endTime) return false;
    return isTimeInRange(time, dayPref.startTime, dayPref.endTime);
  }
  return true;
}

export async function loadNotificationHours() {
  try {
    const { data } = await callAPI("/notificationPreference", {}, "GET");
    if (data?.status && data.data) {
      const preference = Array.isArray(data.data) ? data.data[0] : data.data;
      setNotificationHoursPref(preference || null);
      return;
    }
    setNotificationHoursPref(null);
  } catch (e) {
    console.warn("[webPush] failed to load notification hours", e);
  }
}

export function stripHtml(text) {
  if (!text || typeof text !== "string") return "";
  return text
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, " ")
    .trim();
}

function previewForMessageType(messageType, message) {
  const type = String(messageType || "simple").toLowerCase();
  const plain = stripHtml(message);
  if (plain && plain.startsWith("U2FsdGVkX1")) return "New message";
  if (type === "image") return "📷 Photo";
  if (type === "video") return "🎬 Video";
  if (type === "audio") return "🎵 Audio";
  if (type === "document" || type === "file" || type === "pdf") return "📎 File";
  if (type === "contact") return "👤 Contact";
  if (type === "map" || type === "location") return "📍 Location";
  if (type === "poll") return plain || "Poll";
  return plain || "New message";
}

export function buildChatNotification(data, myId) {
  if (!data) return null;
  const senderId = data.senderId || data.sender;
  if (myId && senderId && String(senderId) === String(myId)) return null;

  const group = data.group || {};
  const groupId = group.id || data.id;
  const isOneToOne = group.isOneToOne === true || data.model === "one";
  const senderName = (data.senderName || "").trim();
  const chatName = stripHtml(data.name || group.name || "Kjuup");
  const bodyText = data.lastReactionEmoji
    ? `${data.lastReactionSenderName || senderName} reacted ${data.lastReactionEmoji}`.trim()
    : previewForMessageType(data.messageType, data.message);

  const title = isOneToOne ? senderName || chatName : chatName || "Kjuup";
  const body = isOneToOne
    ? bodyText
    : senderName
      ? `${senderName}: ${bodyText}`
      : bodyText;

  return {
    title: title || "Kjuup",
    body: body || "New message",
    tag: groupId ? `chat:${groupId}` : "kjuup-chat",
    icon: "/assets/img/logo.png",
    data: {
      model: isOneToOne ? "one" : "group",
      id: groupId,
      chat: data.id || data.chat,
      building: data.buildingList || data.building,
      companyId: data.companyId,
      isInternal: data.isInternal,
      senderName,
    },
  };
}

async function getKjuupRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  const registrations = await navigator.serviceWorker.getRegistrations();
  return (
    registrations.find((reg) => {
      const scriptURL =
        (reg.active && reg.active.scriptURL) ||
        (reg.installing && reg.installing.scriptURL) ||
        (reg.waiting && reg.waiting.scriptURL) ||
        "";
      return scriptURL.indexOf("kjuup-sw.js") !== -1;
    }) || null
  );
}

async function registerSw() {
  if (!("serviceWorker" in navigator)) return null;
  return navigator.serviceWorker.register(SW_PATH, { scope: "/" });
}

async function saveSubscription(subscription) {
  const json = subscription && subscription.toJSON ? subscription.toJSON() : subscription;
  const { data } = await callAPI(
    "/user/updateFcmToken",
    {},
    "PUT",
    {
      webPushSubscription: json ? JSON.stringify(json) : "",
      deviceType: "web",
    }
  );
  if (!data || data.status !== true) {
    throw new Error((data && data.message) || "Failed to save push subscription");
  }
  try {
    if (json) localStorage.setItem(SUBSCRIBED_KEY, "1");
    else localStorage.removeItem(SUBSCRIBED_KEY);
  } catch (e) { /* ignore */ }
}

function waitForActivation(registration, ms = 2500) {
  const worker = registration.active || registration.installing || registration.waiting;
  if (!worker || worker.state === "activated") {
    return Promise.resolve(registration);
  }
  return new Promise((resolve) => {
    const done = () => resolve(registration);
    const t = setTimeout(done, ms);
    worker.addEventListener("statechange", function onChange() {
      if (worker.state === "activated" || worker.state === "redundant") {
        worker.removeEventListener("statechange", onChange);
        clearTimeout(t);
        done();
      }
    });
  });
}

export async function enableWebPush() {
  if (!isWebPushSupported()) {
    return { ok: false, permission: "unsupported" };
  }
  try {
    localStorage.removeItem(DISABLED_KEY);
  } catch (e) { /* ignore */ }

  const permission =
    Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission();
  if (permission !== "granted") {
    return { ok: false, permission };
  }

  // Permission is enough for in-tab OS banners. Web Push subscribe/save is
  // best-effort so a backend hiccup does not block the toggle or the test banner.
  try {
    const registration = await registerSw();
    if (registration) {
      await waitForActivation(registration);
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(webPushConfig.vapidPublicKey),
        });
      }
      await saveSubscription(subscription);
    }
  } catch (e) {
    console.warn("[webPush] subscribe/save failed", e);
  }
  return { ok: true, permission: "granted" };
}

export async function disableWebPush() {
  try {
    localStorage.setItem(DISABLED_KEY, "1");
  } catch (e) { /* ignore */ }
  try {
    const registration = await getKjuupRegistration();
    const subscription = registration && (await registration.pushManager.getSubscription());
    if (subscription) {
      await subscription.unsubscribe();
    }
  } catch (e) { /* ignore */ }
  try {
    await saveSubscription(null);
  } catch (e) { /* ignore */ }
  try {
    localStorage.removeItem(SUBSCRIBED_KEY);
  } catch (e) { /* ignore */ }
  return { ok: true };
}

/**
 * After login: if the user already granted permission and did not turn
 * notifications off in Settings, (re)register the SW and store the
 * subscription on this session.
 */
export async function initWebPush({ onNotificationClick } = {}) {
  if (typeof onNotificationClick === "function") {
    _clickHandler = onNotificationClick;
  }
  if (!_swListenerBound && typeof navigator !== "undefined" && "serviceWorker" in navigator) {
    _swListenerBound = true;
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data && event.data.type === "KJUUP_NOTIFICATION_CLICK" && typeof _clickHandler === "function") {
        _clickHandler(event.data.payload || {});
      }
    });
  }

  loadNotificationHours().catch(() => {});

  if (!isWebPushSupported()) return;
  if (isWebPushDisabledByUser()) return;
  if (Notification.permission !== "granted") return;

  try {
    await enableWebPush();
  } catch (e) {
    console.warn("[webPush] init failed", e);
  }
}

export function parseChatPayloadFromPush(data = {}) {
  const groupId = data.id || data.chatGroupId;
  if (!groupId) return null;
  return {
    chatGroupId: String(groupId),
    messageId: data.chat ? String(data.chat) : undefined,
    roomId: data.building ? String(data.building) : undefined,
    companyId: data.companyId ? String(data.companyId) : undefined,
    isInternal: !!(data.companyId || data.isInternal === true || data.isInternal === "true" || data.isInternal === 1),
  };
}

export function consumeChatQueryParams() {
  try {
    const params = new URLSearchParams(window.location.search);
    const chatGroupId = params.get("chatGroupId");
    if (!chatGroupId) return null;
    const payload = {
      chatGroupId,
      messageId: params.get("messageId") || undefined,
      roomId: params.get("roomId") || undefined,
      companyId: params.get("companyId") || undefined,
      isInternal: params.get("isInternal") === "true" || !!params.get("companyId"),
    };
    const clean = window.location.pathname || "/";
    window.history.replaceState({}, "", clean);
    return payload;
  } catch (e) {
    return null;
  }
}

/**
 * A task push opened in a new window: "/?todoId=…&roomId=…" (kjuup-sw.js).
 * Returns { model: "todo", id, buildingId } and cleans the URL, else null.
 */
export function consumeTodoQueryParams() {
  try {
    const params = new URLSearchParams(window.location.search);
    const todoId = params.get("todoId");
    if (!todoId) return null;
    const payload = { model: "todo", id: todoId, buildingId: params.get("roomId") || undefined };
    window.history.replaceState({}, "", window.location.pathname || "/");
    return payload;
  } catch (e) {
    return null;
  }
}

function chatGroupId(chat) {
  if (!chat) return null;
  return chat.group && chat.group.id ? chat.group.id : chat.id;
}

function isKnownChat(groupId) {
  if (!groupId) return false;
  const want = String(groupId);
  try {
    const { store } = require("../store");
    const state = store.getState() || {};
    const lists = [state.openChats, state.openBottomChats, state.activityMessages];
    return lists.some((list) =>
      (list || []).some((chat) => {
        const id = chatGroupId(chat);
        return id && String(id) === want;
      })
    );
  } catch (e) {
    return false;
  }
}

function absUrl(path) {
  if (!path) return undefined;
  if (/^https?:\/\//i.test(path)) return path;
  try {
    return new URL(path, window.location.origin).href;
  } catch (e) {
    return path;
  }
}

let _lastNotify = { key: "", at: 0 };
let _permWarned = false;

function warnPermissionOnce() {
  if (_permWarned) return;
  _permWarned = true;
  const perm = typeof Notification === "undefined" ? "unsupported" : Notification.permission;
  console.warn("[webPush] notifications blocked, permission=", perm);
}

async function claimLocalNotify(key, ttlMs) {
  const now = Date.now();
  if (_lastNotify.key === key && now - _lastNotify.at < ttlMs) return false;
  _lastNotify = { key, at: now };
  try {
    const lsKey = `kjuup_notify_claim:${key}`;
    const prev = localStorage.getItem(lsKey);
    if (prev) {
      const ts = parseInt(String(prev).split(":")[0], 10);
      if (Number.isFinite(ts) && now - ts < ttlMs) return false;
    }
    const token = `${now}:${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(lsKey, token);
    await new Promise((r) => setTimeout(r, 40));
    if (localStorage.getItem(lsKey) !== token) return false;
  } catch (e) { /* private mode / quota — in-memory claim still applies */ }
  return true;
}

function closeNotificationsSoon(getNotifs, ms = AUTO_CLOSE_MS) {
  setTimeout(() => {
    Promise.resolve()
      .then(getNotifs)
      .then((list) => {
        (list || []).forEach((n) => {
          try { n.close(); } catch (e) { /* already dismissed */ }
        });
      })
      .catch(() => {});
  }, ms);
}

function showViaConstructor(title, options, onClick, data) {
  try {
    const n = new Notification(title, options);
    n.onclick = () => {
      try {
        window.focus();
        n.close();
      } catch (err) { /* ignore */ }
      if (typeof onClick === "function") onClick(data);
    };
    closeNotificationsSoon(() => [n]);
  } catch (e) {
    console.warn("[webPush] Notification ctor failed", e);
  }
}

function showLocalNotification({ title, body, tag, icon, data, onClick, dedupeMs }) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") {
    warnPermissionOnce();
    return;
  }
  if (isWebPushDisabledByUser()) return;
  if (!isWithinNotificationHours()) return;

  const key = tag || title || "kjuup";
  const isEmail = String(key).indexOf("kjuup-email") === 0;
  const ttlMs = Number(dedupeMs) > 0 ? Number(dedupeMs) : (isEmail ? 120000 : 2000);
  const contentKey = isEmail ? `kjuup-email-body:${title}|${body || ""}` : key;

  const options = {
    body: body || "",
    icon: absUrl(icon || "/assets/img/logo.png"),
    badge: absUrl("/assets/img/logo.png"),
    tag: key,
    renotify: !isEmail,
    requireInteraction: false,
    silent: false,
    data: data || {},
  };

  (async () => {
    const claimedTag = await claimLocalNotify(key, ttlMs);
    if (!claimedTag) return;
    if (isEmail && contentKey !== key) {
      const claimedBody = await claimLocalNotify(contentKey, ttlMs);
      if (!claimedBody) return;
    }
    try {
      const registration = (await getKjuupRegistration()) || (await registerSw());
      if (registration) {
        await registration.showNotification(title, options);
        closeNotificationsSoon(() => registration.getNotifications({ tag: key }));
        return;
      }
    } catch (e) {
      console.warn("[webPush] SW notification failed", e);
    }
    showViaConstructor(title, options, onClick, data);
  })();
}

/**
 * Show an OS banner from the open tab. Web Push covers a fully closed browser.
 */
export function notifyIfBackground({ title, body, tag, icon, data, onClick, dedupeMs }) {
  if (typeof document === "undefined") return;
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  showLocalNotification({ title, body, tag, icon, data, onClick, dedupeMs });
}

export function notifyChatIfBackground(latestMsg, myId, onClick) {
  const built = buildChatNotification(latestMsg, myId);
  if (!built) return;
  const recipients = Array.isArray(latestMsg?.unseenChatCount) ? latestMsg.unseenChatCount : [];
  const isForMe = recipients.some((e) => String(e?.userId) === String(myId));
  const groupId = built.data && built.data.id;
  if (!isForMe && !isKnownChat(groupId)) return;
  showLocalNotification({ ...built, onClick });
}
